import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Button, Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../../assets/icons";
import { BodyCopy } from "../../components/BodyCopy";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { FUND_REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

type ProcessingVariant =
  | "loading"
  | "validationFailed"
  | "underReview"
  | "alreadyRegistered"
  | "generic"
  | "rateLimit";

const variantFromPath = (pathname: string): ProcessingVariant => {
  if (pathname.includes("validation-failed")) return "validationFailed";
  if (pathname.includes("under-review")) return "underReview";
  if (pathname.includes("already-registered")) return "alreadyRegistered";
  if (pathname.includes("rate-limit")) return "rateLimit";
  if (pathname.includes("/generic")) return "generic";
  return "loading";
};

/**
 * Card registration transition (gallery-safe).
 *
 * In production this tokenizes, pre-validates, and **registers** the card
 * (no rails) before returning to Transfer as a saved instrument. Confirm
 * only prepares the rail. Gallery routes expose the real error families
 * without network calls.
 */
export const AddCardProcessingPage = memo(() => {
  const { t } = useTranslation("fund-request");
  const { theme } = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const variant = variantFromPath(pathname);
  const colorConfig =
    variant === "loading" ? theme.whiteView : theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    bodyCopy,
    backgroundIcon,
    iconColors,
    title,
  } = colorConfig;

  if (variant !== "loading") {
    const messageKey =
      variant === "validationFailed"
        ? "card.validationFailed"
        : variant === "underReview"
          ? "card.underReview"
          : variant === "alreadyRegistered"
            ? "card.alreadyRegistered"
            : variant === "rateLimit"
              ? "card.rateLimitMessage"
              : "card.registrationFailed";
    const titleKey =
      variant === "rateLimit"
        ? "card.rateLimitTitle"
        : "card.registrationFailedTitle";
    const subtitleKey =
      variant === "rateLimit"
        ? "card.rateLimitSubtitle"
        : "card.registrationFailedSubtitle";

    return (
      <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t(titleKey)}
                highlightColor={title}
                secudnary={t(subtitleKey)}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>{t(messageKey)}</BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(FUND_REQUEST_ROUTES.ADD_CARD)}
            >
              {t("card.tryAgain")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
